import React, { forwardRef, type ReactNode, useCallback, useMemo } from 'react';

import classnames from 'classnames';

import useControlledState from '../../../hooks/useControlledState';
import RadioGroupContext from '../context';
import { type RadioChangeEvent } from '../Radio';

import styles from './RadioGroup.module.scss';

export type RadioGroupProps = {
    /**
     * The `name` property of all `input[type="radio"]` children.
     * It is usually used to let the browser see your RadioGroup
     * as a real "group" and keep the default behavior.
     * For example, using left/right keyboard arrow to change
     * your selection in the same RadioGroup.
     */
    name?: string;
    /**
     * List of Radio options to render.
     */
    children?: ReactNode;
    /**
     * Disable all radio buttons.
     */
    disabled?: boolean;
    /**
     * The current selected value.
     */
    value?: any;
    /**
     * A callback function that is triggered when the state is changed.
     */
    onChange?: (event: RadioChangeEvent) => void;
    /**
     * List the options vertically instead of horizontally
     */
    vertical?: boolean;
    /**
     * Specifies the initial state: which radio button is selected (only for uncontrolled mode).
     */
    defaultValue?: any;
    /**
     * Specifies the custom classname
     */
    className?: string;
};

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
    (props: RadioGroupProps, ref) => {
        const {
            onChange,
            name,
            children,
            className,
            disabled,
            value: controlledValue,
            vertical,
            defaultValue,
            ...rest
        } = props;

        const [value, setValue] = useControlledState(controlledValue, defaultValue, 'RadioGroup');

        const onRadioChange = useCallback(
            (ev: RadioChangeEvent) => {
                const lastValue = value;
                const val = ev.target.value;
                setValue(val);
                if (val !== lastValue) {
                    onChange?.(ev);
                }
            },
            [value, setValue, onChange]
        );

        const memoizedContextValue = useMemo(
            () => ({
                value,
                onChange: onRadioChange,
                name,
                disabled
            }),
            [value, disabled, name, onRadioChange]
        );

        return (
            <div
                ref={ref}
                className={classnames(
                    styles.container,
                    {
                        [styles.disabled]: disabled,
                        [styles.vertical]: vertical
                    },
                    className
                )}
                {...rest}
            >
                <RadioGroupContext.Provider value={memoizedContextValue}>
                    {children}
                </RadioGroupContext.Provider>
            </div>
        );
    }
);

RadioGroup.displayName = 'RadioGroup';
